import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Injector,
  Input,
  OnInit,
  Output,
  Renderer2,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  FbSignatureWidget,
  FbTextareaWidget,
  ImageProperty,
  WidgetActionData,
} from '../../../models';

import { DomSanitizer } from '@angular/platform-browser';
import { WebviewService } from '@nrwl/rx-webview-service';
import { Widget } from '../../../models/form-widget.model';
import { TemplateMode, WidgetBase } from '../../../models/widget-base';
import { SnackbarService } from '@nrwl/snackbar-services';
import { MatDialog } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TokenToDivPipe } from '../../../helpers/token-to-div.pipe';
import { DateFormatPipe } from '../../../helpers/date-format.pipe';

@Component({
  selector: 'app-fb-signature',
  templateUrl: './fb-signature.component.html',
  styleUrls: ['./fb-signature.component.scss'],
  imports: [CommonModule, FormsModule, TokenToDivPipe, DateFormatPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FbSignatureComponent extends WidgetBase implements OnInit, AfterViewInit {
  @ViewChild('contentTemplate', { static: false })
  contentTemplate!: TemplateRef<any>;
  private container!: HTMLElement;
  suppressPrompt: boolean;
  // Unique ID to determine which signature signed by iOS
  // public UID = 'uid' + Math.random().toString(36).substring(2)

  @Input() public override set payload(value) {
    this._payload = value;
    this.applyMetadata();

    this.ready.next(true);
  }
  public override get payload() {
    return this._payload;
  }

  @Input() public override set entity(value) {
    this._entity = value;
  }
  public override get entity() {
    return this._entity;
  }

  get isMobile() {
    return (window as any).webkit;
  }

  get isKiosk() {
    return this.mode === 'kiosk' || this.isRxPortal;
  }

  get isClientTypeSignature() {
    return (
      this.payload.properties.signType?.value === '2' ||
      this.payload.properties.signType?.value === '0'
    );
  }

  @Output() signEvent = new EventEmitter<any>();

  // iOS event: Signature recieved
  @HostListener('window:rxEventFormSigned', ['$event'])
  rxEventFormSigned(event) {
    if (event.detail.signType === this.payload.properties.id?.value) {
      this.payload.properties.value.value = event.detail.data;
      this.resetSigneeInfo();
      this.changeDetectorRef.detectChanges();
    }
  }

  constructor(
    public sanitizer: DomSanitizer,
    public changeDetectorRef: ChangeDetectorRef,
    public webviewService: WebviewService,
    public snackbarService: SnackbarService,
    private injector: Injector,
    private dialog: MatDialog,
    private renderer: Renderer2,
  ) {
    super(injector);
  }

  ngOnInit() {
    if (!this.isDesign) {
      this.payload.properties.value.original = this.payload.properties.value.value;
    }
  }

  ngAfterViewInit(): void {}

  public resetSigneeInfo() {
    const signee = this.getSignee();
    if (signee) {
      signee.signed_at = null;
      signee.metadata.ip_address = null;
    }
  }

  public applyMetadata() {
    if (!this.payload.properties.id?.value) {
      this.payload.properties.id.value = 'uid' + Math.random().toString(36).substring(2);
    }
  }

  public getClasses() {
    const classes: any = [];
    switch (this.payload.properties.direction.value) {
      case 'left': {
        classes.push('flex-row aife');
        break;
      }
      case 'right': {
        classes.push('flex-row-reverse aife');
        break;
      }
      case 'bottom': {
        classes.push('flex-column-reverse');
        break;
      }
    }

    if (this.payload.properties.border.value) {
      classes.push('border');
    }

    return classes.join(' ');
  }

  public getBackground() {
    return this.sanitizer.bypassSecurityTrustStyle(`url(${this.payload.properties.value.value})`);
  }

  public isSigned() {
    const widget = new FbSignatureWidget(this.payload);
    return widget.hasSignature();
  }

  // iOS event
  public sign() {
    if (!this.canSign()) {
      this.promptRestrictedAlert();
      return;
    }

    const status = this.webviewService.sign({
      signType: this.payload.properties.id?.value,
    });
    if (status === -1) {
      // webviewService didn't detect webview then try to sign through browser
      this.action.emit(
        new WidgetActionData(this, {
          signType: this.payload.properties.id?.value,
        }),
      );
    }
  }

  public canSign() {
    return true;
    //RESERVED FOR FUTURE USE
    // Prevent client to sign for doctor in kiosk mode
    if (!this.isClientTypeSignature && (this.isKiosk || this.isRxPortal)) {
      return false;
    }

    // Prevent doctor to sign client designated signatures
    if (this.isClientTypeSignature && !this.isKiosk) {
      return false;
    }
    return true;
  }

  public getTemplateMode(): TemplateMode {
    if (this.isPrinting) {
      return TemplateMode.Printing;
    }

    if (this.isRxPortal) {
      return TemplateMode.RxPortal;
    }

    if (this.isMobile) {
      return this.isKiosk ? TemplateMode.MobileKiosk : TemplateMode.MobileProvider;
    }

    return TemplateMode.Web;
  }

  public canShowSigneeInfo(): boolean {
    if (this.isDesign) {
      return false;
    }
    return this.isSigned() && this.getSigneeDate()
      ? true
      : false && this.getSigneeIP()
        ? true
        : false;
  }

  override isValid() {
    return true;
    // RESERVED FOR FUTURE USE
    const widget = new FbSignatureWidget(this.payload, !this.isKiosk && !this.isRxPortal);
    if (widget.isValid()) {
      return true;
    }

    return false;
  }

  isRequired() {
    const widget = new FbSignatureWidget(this.payload);
    return widget.isRequired();
  }

  promptRestrictedAlert() {
    // Create a new div element
    this.container = this.renderer.createElement('div');

    // Optionally add classes or styles to the div
    this.renderer.addClass(this.container, 'fe-alert');

    // Append the new div to the body
    this.renderer.appendChild(document.body, this.container);

    // Render the template inside the created div
    this.renderTemplate();
  }

  private renderTemplate() {
    // Create a view of the template and attach it to the created div
    const embeddedView = this.contentTemplate.createEmbeddedView(null);
    embeddedView.detectChanges();

    // Append each node to the created div
    embeddedView.rootNodes.forEach((node) => {
      this.renderer.appendChild(this.container, node);
    });
  }

  closeRestrictedAlert() {
    this.renderer.removeChild(document.body, this.container);
  }

  getSignee() {
    try {
      const meta = this.entity?.metadata?.signees?.find(
        (item) => item.widget_id === this.payload.properties.id.value,
      );
      const signee = this.entity?.signees?.find((item) => +item.id === +meta?.signee_id);
      return signee;
    } catch (e) {
      return null;
    }
  }

  getSigneeName() {
    return '';
  }

  getSigneeDate() {
    const signee = this.getSignee();
    return signee?.signed_at || '';
  }

  getSigneeIP() {
    const signee = this.getSignee();
    return signee?.metadata?.ip_address || '';
  }
}
